import { Link } from "@tanstack/react-router";
import type { PlayerStats } from "@/api/ranking";
import type { ColumnDef } from "@tanstack/react-table";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/shadcn/tooltip";
import { getPointsBadge } from "@/lib/utils";

export const overviewStatsTableColumns: ColumnDef<PlayerStats>[] = [
    {
        id: "rank",
        header: "Rank",
        size: 72,
        cell: ({ row, table }) => {
            if (row.original.playerId < 0) {
                return <span className="font-semibold text-on-surface-variant">-</span>;
            }

            const allData = table.getCoreRowModel().rows;
            const sortedData = [...allData]
                .filter((r) => r.original.playerId >= 0)
                .sort((a, b) => b.original.totalPoints - a.original.totalPoints);

            const rank = sortedData.findIndex((r) => r.id === row.id) + 1;
            const label = `#${rank.toString().padStart(2, "0")}`;

            return (
                <span
                    className={`font-headline font-bold text-sm ${
                        rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"
                    }`}
                >
                    {label}
                </span>
            );
        },
    },
    {
        id: "player",
        header: "Player name",
        size: 148,
        cell: ({ row }) => {
            if (row.original.playerId < 0) {
                return (
                    <span className="font-semibold text-on-surface-variant">
                        {row.original.firstName} {row.original.lastName}
                    </span>
                );
            }
            return (
                <Link
                    to="/player/$playerId"
                    params={{ playerId: row.original.playerId.toString() }}
                    className="font-bold text-on-surface hover:text-primary hover:underline transition-colors"
                >
                    {row.original.firstName} {row.original.lastName}
                </Link>
            );
        },
    },
    {
        accessorKey: "line",
        header: "Line",
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span className="text-sm text-on-surface">{value === 0 ? "N/A" : value}</span>;
        },
    },
    {
        accessorKey: "role",
        header: "Role",
        size: 82,
        cell: ({ getValue }) => {
            const value = getValue() as string;
            return (
                <span className="px-2 py-0.5 bg-surface-container-highest rounded text-[9px] font-bold uppercase text-on-surface-variant">
                    {value}
                </span>
            );
        },
    },
    {
        accessorKey: "pointsDefensive",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Defensive</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>
                        Defensive performance metrics covering pressure application, save percentage, puck battles, shot
                        blocking, and clearances.
                    </p>
                </TooltipContent>
            </Tooltip>
        ),
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span className="text-sm">{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsOffensive",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Offensive</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>
                        Attacking performance metrics including shooting accuracy, passing ability, goal creation, and
                        puck handling skills.
                    </p>
                </TooltipContent>
            </Tooltip>
        ),
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span className="text-sm">{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsTeamPlay",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Team Play</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>
                        Team contribution metrics measuring discipline, plus/minus rating, shot attempt differentials,
                        and puck management.
                    </p>
                </TooltipContent>
            </Tooltip>
        ),
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span className="text-sm">{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsEffort",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Effort</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>
                        Physical performance metrics tracking speed, distance covered, puck handling speed, and movement
                        intensity.
                    </p>
                </TooltipContent>
            </Tooltip>
        ),
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span className="text-sm">{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "pointsBonus",
        header: () => (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="cursor-help">Bonus</span>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Additional points awarded for goals and assists scored during the game.</p>
                </TooltipContent>
            </Tooltip>
        ),
        cell: ({ getValue }) => {
            const value = getValue() as number;
            return <span className="text-sm">{value === 0 ? "-" : value}</span>;
        },
    },
    {
        accessorKey: "totalPoints",
        header: "Total Points",
        size: 112,
        cell: ({ getValue }) => {
            const value = getValue() as number;
            if (value === 0) {
                return (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="cursor-help text-on-surface-variant">-</span>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>
                                Player has spent insufficient time on the ice to be objectively evaluated against our
                                KPI scale.
                            </p>
                        </TooltipContent>
                    </Tooltip>
                );
            }
            const { bg, text, border } = getPointsBadge(value);
            return (
                <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${bg} ${text} rounded font-bold text-sm border ${border}`}
                >
                    {value}
                </div>
            );
        },
    },
];
